import { useOnChange } from "@prismicio/editor-support/React"
import { getUrlTitle, toURL } from "@prismicio/editor-support/URL"
import {
  ErrorBoundary,
  Field,
  FieldControl,
  FieldIcon,
  FieldLabel,
  Popover,
  type TextInputProps
} from "@prismicio/editor-ui"
import {
  FilledDocumentLinkContent,
  FilledImageLinkContent,
  FilledLinkContent,
  type LinkContent
} from "@prismicio/types-internal/lib/content"
import type { Link } from "@prismicio/types-internal/lib/customtypes"
import { type PropsWithChildren, Suspense, useState } from "react"

import { useEditorConfig } from "../../EditorConfig"
import { FieldTextInput } from "../FieldTextInput"
import { MediaDialog } from "../ImageField/MediaDialog"
import { getFieldLabel } from "../utils"
import { DocumentsModal } from "./Documents/DocumentsModal"
import { LinkDescriptionCard } from "./LinkDescriptionCard"
import { type FieldSelectType, LinkFieldActions } from "./LinkFieldActions"
import { LinkImageCard } from "./LinkImageCard"
import { useDocumentTitle, useLinkField } from "./useLinkField"
import { WebLinkModal } from "./WebLinks/WebLinkModal"

interface LinkFieldProps {
  id?: string
  field: Link
  content: LinkContent | undefined
  condensedButtons?: boolean
  onContentChange: (content: LinkContent | undefined) => void
}

export function LinkField(props: LinkFieldProps) {
  const {
    content,
    field,
    id,
    onContentChange,
    condensedButtons = false
  } = props

  const [isOverField, setIsOverField] = useState(false)
  const [isOverPopover, setIsOverPopover] = useState(false)
  const [openLinkModal, setOpenLinkModal] = useState<FieldSelectType>()

  const {
    contentType,
    fieldValue,
    errorBoundaryRef,
    onDocumentSelected,
    onError,
    onMediaSelected,
    onValueChange,
    selectType,
    onTypeChange,
    isTypeSelectEnabled,
    displayTextEnabled,
    ...textInputProps
  } = useLinkField({ content, field, onContentChange })

  // If no link was set after closing a modal, reset the type
  useOnChange(openLinkModal, () => {
    if (
      selectType !== undefined &&
      !openLinkModal &&
      !fieldValue &&
      isTypeSelectEnabled
    )
      onTypeChange(undefined)
  })

  const isPopoverOpen = isOverField || isOverPopover

  const hasDocumentValue = contentType === "DocumentLink" && fieldValue !== ""
  const isWebLink = contentType === "ExternalLink"
  const validWebLinkUrl = isWebLink && toURL(fieldValue)
  const allowTargetBlank = Boolean(field.config?.allowTargetBlank)

  function onOpenLinkModal() {
    setIsOverField(true)
    switch (contentType) {
      case "DocumentLink":
        return setOpenLinkModal("document")
      case "ExternalLink":
        return setOpenLinkModal("web")
      case "FileLink":
      case "ImageLink":
      case "MediaLink":
        return setOpenLinkModal("media")
      default:
        return setOpenLinkModal(undefined)
    }
  }

  const sharedInputProps: LinkFieldTextInputProps = {
    ...textInputProps,
    id,
    onBlur: () => setIsOverField(false),
    onFocus: () => {
      setIsOverField(true)
    },
    onOpenLinkModal,
    onUnlink: () => {
      if (isTypeSelectEnabled) onTypeChange(undefined)
      else onValueChange("")
    },
    onSelectValueChange: (newValue: FieldSelectType) => {
      onTypeChange(newValue)
      setOpenLinkModal(newValue)
    },
    value: fieldValue,
    condensedButtons,
    allowTargetBlank,
    selectType,
    onValueChange,
    isTypeSelectEnabled,
    displayTextEnabled,
    onKeyDown(e) {
      if (displayTextEnabled) return
      if (e.key === "Enter" || e.key === " ") {
        onOpenLinkModal()
        // avoid typing a space in the input in the newly opened modal
        e.preventDefault()
      }
    },
    onClick: () => {
      if (displayTextEnabled) return
      onOpenLinkModal()
    }
  }

  const { searchDocuments } = useEditorConfig()

  return (
    <>
      <Popover
        onMouseOverChange={setIsOverPopover}
        open={isPopoverOpen}
        align="start"
        sideOffset={-3}
        openDelay={100}
        closeDelay={200}
        focusOnEnter={false}
        anchor={
          <Field onMouseOverChange={setIsOverField}>
            <FieldIcon
              name={getFieldIcon({ selectType, displayTextEnabled })}
            />
            <FieldLabel htmlFor={id}>{getFieldLabel(field)}</FieldLabel>
            <FieldControl>
              {hasDocumentValue && searchDocuments ? (
                <ErrorBoundaryAndSuspenseWrapper
                  errorBoundaryRef={errorBoundaryRef}
                  onError={onError}
                  fallback={
                    <LinkFieldTextInput {...sharedInputProps} value="..." />
                  }
                  renderError={() => renderError(sharedInputProps)}
                >
                  <DocumentContent
                    {...sharedInputProps}
                    documentId={fieldValue}
                  />
                </ErrorBoundaryAndSuspenseWrapper>
              ) : (
                <LinkFieldTextInput {...sharedInputProps} />
              )}
            </FieldControl>
          </Field>
        }
      >
        {validWebLinkUrl && (
          <LinkDescriptionCard
            url={validWebLinkUrl}
            title={getUrlTitle(validWebLinkUrl) ?? ""}
            subTitle={validWebLinkUrl.toString()}
          />
        )}

        {content && FilledImageLinkContent.is(content.value) && (
          <LinkImageCard
            url={content.value.url}
            title={content.value.name}
            subTitle={`${content.value.width} x ${content.value.height} pixels`}
          />
        )}
      </Popover>

      <WebLinkModal
        open={openLinkModal === "web"}
        setOpen={(open) => setOpenLinkModal(open ? "web" : undefined)}
        url={fieldValue}
        openInNewTab={textInputProps.isTargetNewTab}
        showNewTabButton={Boolean(field.config?.allowTargetBlank)}
        onUpdate={(newUrl: string, newTab: boolean) =>
          onValueChange(newUrl, newTab)
        }
      />

      <MediaDialog
        open={openLinkModal === "media"}
        onOpenChange={(open) => setOpenLinkModal(open ? "media" : undefined)}
        onMediaSelected={onMediaSelected}
        assetType="all"
      />

      <DocumentsModal
        open={openLinkModal === "document"}
        onOpenChange={(open) => setOpenLinkModal(open ? "document" : undefined)}
        onSelectDocument={(documentId) => {
          setOpenLinkModal(undefined)
          onDocumentSelected(documentId)
        }}
        fixedCustomTypes={new Set(field.config?.customtypes)}
        fixedTags={new Set(field.config?.tags)}
        linkedDocumentId={
          content && FilledDocumentLinkContent.is(content.value)
            ? content.value.id
            : undefined
        }
      />
    </>
  )
}

function renderError(props: LinkFieldTextInputProps) {
  return <LinkFieldTextInput {...props} />
}

type BaseInputProps = {
  id?: string
  isTypeSelectEnabled: boolean
  selectType?: FieldSelectType
  condensedButtons: boolean
  onFocus: () => void
  onSelectValueChange: (newValue: FieldSelectType) => void
  onOpenLinkModal: () => void
  onUnlink: () => void
  displayTextEnabled: boolean
  displayText: string
  onDisplayTextChange: (newValue: string) => void
  onClick?: TextInputProps["onClick"]
  onKeyDown?: TextInputProps["onKeyDown"]
}

type LinkFieldTextInputProps = BaseInputProps & {
  placeholder?: string
  value: string
  isTargetNewTab: boolean
  onValueChange: (newValue: string) => void
  onBlur: () => void
  allowTargetBlank: boolean
}

type GetFieldTextInputPropsReturnType = {
  onValueChange?: LinkFieldTextInputProps["onValueChange"]
  placeholder: LinkFieldTextInputProps["placeholder"]
  readOnly: boolean
  value: LinkFieldTextInputProps["value"]
  variant: "normal" | "link"
  cursor: "text" | "pointer" | "default"
}

function getFieldTextInputProps(
  props: LinkFieldTextInputProps
): GetFieldTextInputPropsReturnType {
  return {
    onValueChange: props.displayTextEnabled
      ? props.onDisplayTextChange
      : props.onValueChange,
    placeholder: props.displayTextEnabled
      ? "Add your display text"
      : props.placeholder,
    readOnly: !props.displayTextEnabled,
    value: props.displayTextEnabled ? props.displayText : props.value,
    variant: props.displayTextEnabled ? "normal" : "link",
    cursor: getCursor({
      displayTextEnabled: props.displayTextEnabled,
      isTypeSelectEnabled: props.isTypeSelectEnabled,
      value: props.value
    })
  }
}

function LinkFieldTextInput(props: LinkFieldTextInputProps) {
  const {
    id,
    value,
    onOpenLinkModal,
    onBlur,
    isTypeSelectEnabled,
    selectType,
    onSelectValueChange,
    onUnlink,
    isTargetNewTab,
    condensedButtons,
    allowTargetBlank,
    displayTextEnabled,
    onFocus,
    onClick,
    onKeyDown
  } = props

  const fieldTextInputProps = getFieldTextInputProps(props)

  return (
    <FieldTextInput
      id={id}
      {...fieldTextInputProps}
      onBlur={onBlur}
      onFocus={onFocus}
      onClick={onClick}
      onKeyDown={onKeyDown}
      textOverflow="ellipsis"
      endAdornment={
        <LinkFieldActions
          hasLink={Boolean(value && selectType)}
          onUnlink={onUnlink}
          selectEnabled={isTypeSelectEnabled}
          selectedType={selectType}
          value={value}
          onOpenLinkModal={onOpenLinkModal}
          onValueChange={onSelectValueChange}
          openInNewTab={isTargetNewTab}
          condensedButtons={condensedButtons}
          showNewTabButton={allowTargetBlank && selectType === "web"}
          displayTextEnabled={displayTextEnabled}
        />
      }
    />
  )
}

type DocumentContentProps = BaseInputProps & {
  documentId: string
}

function getDocumentFieldTextInputProps(
  props: DocumentContentProps,
  documentTitle: string
): GetFieldTextInputPropsReturnType {
  return {
    onValueChange: props.displayTextEnabled
      ? props.onDisplayTextChange
      : undefined,
    placeholder: props.displayTextEnabled ? "Add your display text" : undefined,
    readOnly: !props.displayTextEnabled,
    value: props.displayTextEnabled ? props.displayText : documentTitle,
    variant: props.displayTextEnabled ? "normal" : "link",
    cursor: getCursor({
      displayTextEnabled: props.displayTextEnabled,
      isTypeSelectEnabled: props.isTypeSelectEnabled,
      value: documentTitle
    })
  }
}

function DocumentContent(props: DocumentContentProps) {
  const {
    id,
    documentId,
    onOpenLinkModal,
    isTypeSelectEnabled,
    selectType,
    displayTextEnabled,
    condensedButtons,
    onSelectValueChange,
    onUnlink,
    onFocus,
    onClick,
    onKeyDown
  } = props

  const documentTitle = useDocumentTitle(documentId)

  const documentFieldTextInputProps = getDocumentFieldTextInputProps(
    props,
    documentTitle
  )

  return (
    <FieldTextInput
      id={id}
      {...documentFieldTextInputProps}
      onFocus={onFocus}
      onClick={onClick}
      onKeyDown={onKeyDown}
      textOverflow="ellipsis"
      endAdornment={
        <LinkFieldActions
          hasLink
          onUnlink={onUnlink}
          selectEnabled={isTypeSelectEnabled}
          selectedType={selectType}
          value={documentTitle}
          onOpenLinkModal={onOpenLinkModal}
          condensedButtons={condensedButtons}
          onValueChange={onSelectValueChange}
          displayTextEnabled={displayTextEnabled}
        />
      }
    />
  )
}

interface ErrorBoundaryAndSuspenseWrapperProps extends PropsWithChildren {
  errorBoundaryRef: React.RefObject<ErrorBoundary>
  onError: VoidFunction
  renderError: () => React.ReactNode
  fallback: React.ReactNode
}

function ErrorBoundaryAndSuspenseWrapper(
  props: ErrorBoundaryAndSuspenseWrapperProps
) {
  const { errorBoundaryRef, onError, children, fallback, renderError } = props

  return (
    <ErrorBoundary
      ref={errorBoundaryRef}
      onError={onError}
      renderError={renderError}
    >
      <Suspense fallback={fallback}>{children}</Suspense>
    </ErrorBoundary>
  )
}

interface GetFieldIconArgs {
  selectType?: FieldSelectType
  displayTextEnabled?: boolean
}

export function getFieldIcon(args: GetFieldIconArgs) {
  const { selectType, displayTextEnabled } = args

  if (displayTextEnabled) return "notes"
  switch (selectType) {
    case "document":
      return "description"
    case "web":
      return "public"
    case "media":
      return "image"
    default:
      return "link"
  }
}

interface LinkFieldActionsArgs {
  displayTextEnabled: boolean
  isTypeSelectEnabled: boolean
  value: string
}

function getCursor(args: LinkFieldActionsArgs) {
  const { displayTextEnabled, isTypeSelectEnabled, value } = args
  if (displayTextEnabled) return "text" as const
  if (isTypeSelectEnabled && !value) return "default" as const
  return "pointer" as const
}

/*
 * Similar to the LinkField component, but ensuring the content is always filled.
 * It also ensures that the display text can't be used.
 */

interface FilledLinkFieldProps {
  id?: string
  condensedButtons?: boolean
  field: Link & { config?: { allowText?: false } }
  content: FilledLinkContent | undefined
  onContentChange: (content: FilledLinkContent | undefined) => void
}

export function FilledLinkField(props: FilledLinkFieldProps) {
  const { onContentChange: onFilledContentChange, ...otherProps } = props

  function onContentChange(content: LinkContent | undefined) {
    const filledContent = FilledLinkContent.is(content) ? content : undefined
    onFilledContentChange(filledContent)
  }

  return <LinkField {...otherProps} onContentChange={onContentChange} />
}
